/**
 * DeleteRenderer — read-only render for DELETE statements. Handles the plain
 * `DELETE FROM target …` form, `DELETE … USING …` (PG / MySQL form B), and
 * MySQL multi-table forms — `DELETE t1, t2 FROM t1 INNER JOIN t2 …` (form A)
 * and `DELETE FROM t1 INNER JOIN t2 …` (target tail).
 *
 * Section layout:
 *   - Delete-target card (red border + trash icon — destructive visual cue)
 *   - Tables section (only when a multi-table tail is present after FROM)
 *   - Using section (only when PG / MySQL form B)
 *   - Where pills — or, with no WHERE, a red one-line WHERE row (and a red
 *     "No WHERE" badge on the target row) saying every row is deleted
 *   - Returning card (when present)
 *
 * If parsing fails (no target), renders a small fallback
 * surface — the Visual view still offers Edit as SQL.
 */

import type { ParseOptions, StatementInfo } from '../../../../sql-grammar/model';
import type { VqeEditContext } from '../../edit/editContext';
import { DmlChangeTablePicker } from '../common/DmlChangeTablePicker';
import { DmlNoWhereBadge } from '../common/DmlNoWhereBadge';
import { DmlTargetCard } from '../common/DmlTargetCard';
import { dmlWhereScope } from '../common/dmlWhereScope';
import { DmlParseErrorPanel } from '../fallback/DmlParseErrorPanel';
import { ReturningCard } from '../insert/ReturningCard';
import { FilterPills } from '../select/FilterPills';
import { vqeCopy } from '../../ux/vqeCopy';
import {
  buildAliasCandidatesFromText,
  formatDmlTargetText,
  resolveDmlTargetAlias,
} from '../update/resolveDmlTargetAlias';
import { TablesWithJoinsCard } from '../update/TablesWithJoinsCard';
import { extractDeleteOutline } from './extractDeleteOutline';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export interface DeleteRendererProps {
  readonly statement: StatementInfo;
  readonly bufferText: string;
  readonly options: ParseOptions;
  /** Escape to raw SQL. Surfaced on the parse-error panel; the workspace always threads it. */
  readonly onEditAsSql?: () => void;
  readonly drillTabId?: string;
  readonly editContext?: VqeEditContext;
}

export function DeleteRenderer({
  statement,
  bufferText,
  options,
  onEditAsSql,
  editContext,
}: DeleteRendererProps) {
  const outline = extractDeleteOutline(bufferText, statement.range, options);

  if (!outline.hasDelete || outline.targetText === null) {
    return <DmlParseErrorPanel kind="delete" onEditAsSql={onEditAsSql ?? (() => undefined)} />;
  }

  const hasPreFromTargets = outline.preFromTargetText !== null;
  const hasMultiTable =
    outline.multiTableStart !== null && outline.multiTableEnd !== null;
  const hasUsing = outline.usingStart !== null && outline.usingEnd !== null;
  const hasWhere = outline.whereStart !== null && outline.whereEnd !== null;
  const hasReturning =
    outline.returningStart !== null && outline.returningEnd !== null;
  const whereScope = dmlWhereScope(outline);

  // DML-14/15 parity: mssql's alias-target DELETE (`DELETE p FROM
  // Production.Product p …`) shows a bare, information-free `p` pill unless
  // resolved against the post-FROM target text (which already carries the
  // real relation + alias — see extractDeleteOutline's docstring). Skip a
  // genuine multi-target comma list (`DELETE t1, t2 FROM …`) — that's real
  // table names, not an alias.
  let destructiveTargetText = hasPreFromTargets
    ? (outline.preFromTargetText as string)
    : outline.targetText;

  if (
    hasPreFromTargets &&
    outline.preFromTargetText !== null &&
    !outline.preFromTargetText.includes(',') &&
    outline.targetStart !== null
  ) {
    const tailEnd = (outline.multiTableEnd ?? outline.targetEnd) as number;
    const candidates = buildAliasCandidatesFromText(
      bufferText,
      outline.targetStart,
      tailEnd,
      options,
    );
    const resolved = resolveDmlTargetAlias(outline.preFromTargetText, candidates);
    destructiveTargetText = formatDmlTargetText(outline.preFromTargetText, resolved);
  }

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-delete-renderer"
      data-has-pre-from-targets={hasPreFromTargets ? 'true' : 'false'}
      data-has-multi-table={hasMultiTable ? 'true' : 'false'}
      data-has-using={hasUsing ? 'true' : 'false'}
      data-has-where={hasWhere ? 'true' : 'false'}
      data-has-returning={hasReturning ? 'true' : 'false'}
    >
      <DmlTargetCard
        intent="delete"
        variant="full"
        targetText={destructiveTargetText}
        targetAction={
          <>
            <DmlNoWhereBadge verb="DELETE" targetText={destructiveTargetText} scope={whereScope} />
            <DmlChangeTablePicker
              target="delete"
              editContext={editContext}
              // `DELETE p FROM …` / `DELETE t1, t2 FROM …` name their targets
              // before FROM; swapping the FROM table alone would leave those
              // names pointing at nothing.
              blockedReason={hasPreFromTargets ? vqeCopy.dmlTarget.disabled.targetList : undefined}
            />
          </>
        }
      />

      {hasPreFromTargets ? (
        <TablesWithJoinsCard
          bufferText={bufferText}
          start={outline.targetStart as number}
          end={(outline.multiTableEnd ?? outline.targetEnd) as number}
          options={options}
          title="From"
          testIdPrefix="vqe-delete-from"
        />
      ) : hasMultiTable && outline.multiTableStart !== null && outline.multiTableEnd !== null ? (
        <TablesWithJoinsCard
          bufferText={bufferText}
          start={outline.multiTableStart}
          end={outline.multiTableEnd}
          options={options}
          title="Tables"
          testIdPrefix="vqe-delete-tables"
          fallbackLeadingText={outline.targetText}
        />
      ) : null}

      {hasUsing && outline.usingStart !== null && outline.usingEnd !== null ? (
        <TablesWithJoinsCard
          bufferText={bufferText}
          start={outline.usingStart}
          end={outline.usingEnd}
          options={options}
          title="Using"
          testIdPrefix="vqe-delete-using"
        />
      ) : null}

      {/* With no WHERE this is the one-line red WHERE row: it says every row
          is deleted and carries the Add condition that fixes it. */}
      <FilterPills
        bufferText={bufferText}
        span={
          hasWhere && outline.whereStart !== null && outline.whereEnd !== null
            ? { start: outline.whereStart, end: outline.whereEnd }
            : null
        }
        options={options}
        kind="where"
        editContext={editContext}
        density="compact"
        destructiveGuard={{ verb: 'DELETE', targetText: destructiveTargetText, scope: whereScope }}
      />

      {hasReturning && outline.returningStart !== null && outline.returningEnd !== null ? (
        <ReturningCard
          bufferText={bufferText}
          start={outline.returningStart}
          end={outline.returningEnd}
          options={options}
          testIdPrefix="vqe-delete-returning"
        />
      ) : null}
    </div>
  );
}
